import {
  CACHE_KEYS,
  getFromCache,
  saveToCache,
  isCacheExpired,
  updateCacheTime,
  clearCacheAndTime,
  isSafariOrIOS,
} from '@/utils/CacheHelper'
import { useEndpoints } from '@/composables'
import { httpClient } from '@/services'

const STORE_NAME = CACHE_KEYS.STORE.ORG_EMAIL_SETTINGS
const CACHE_TIME_KEY = CACHE_KEYS.TIME.ORG_EMAIL_SETTINGS
const API_URL = (orgId) => useEndpoints.settings.email.index(orgId)

// Store ongoing request promise
let inFlightRequest = null

export async function getOrgEmailSettingsData(
  orgId,
  cacheTime = CACHE_KEYS.EXPIRE_TIME.THREE_DAYS
) {
  // ----------------------------
  // SAFARI: ALWAYS FETCH FROM API (no IndexedDB)
  // ----------------------------
  if (isSafariOrIOS()) {
    // if a request is already in-flight, reuse it
    if (inFlightRequest) {
      return inFlightRequest
    }

    // arrow function (ESLint-safe)
    const fetchDirectFromApi = async () => {
      try {
        const res = await httpClient
          .get(API_URL(orgId))
          .then((r) => r)
          .catch((err) => {
            console.log('org-email-settings-fetch-err', {
              err,
            })
            return {}
          })
        const batch = res.data
        // DO NOT save to cache for Safari
        return batch
      } finally {
        // Clear in-flight request reference once complete
        inFlightRequest = null
      }
    }

    inFlightRequest = fetchDirectFromApi()
    return inFlightRequest
  }

  // ----------------------------
  // NON-SAFARI: original flow (cache-first)
  // ----------------------------

  // If cache is still valid → return it
  if (!isCacheExpired(CACHE_TIME_KEY, cacheTime)) {
    const objArray = await getFromCache(STORE_NAME)
    delete objArray[0].id
    return objArray[0]
  }

  // If request already in-flight → return the same promise
  if (inFlightRequest) {
    return inFlightRequest
  }

  // Clear cache before fetching fresh data
  await clearCacheAndTime(STORE_NAME, CACHE_TIME_KEY)

  async function fetchNextBatch() {
    const res = await httpClient
      .get(API_URL(orgId))
      .then((res) => res)
      .catch((err) => {
        console.log('org-email-settings-fetch-err', {
          err,
        })
        return {}
      })
      .finally(() => {
        // Clear in-flight request reference once complete
        inFlightRequest = null
      })

    const batch = [{ ...res.data, id: crypto.randomUUID() }]

    await saveToCache(STORE_NAME, batch)

    updateCacheTime(CACHE_TIME_KEY)

    const objArray = await getFromCache(STORE_NAME)
    delete objArray[0].id
    return objArray[0]
  }

  // Store the in-flight promise so others can await it
  inFlightRequest = fetchNextBatch()
  return inFlightRequest
}
